import { useLocation, useNavigate } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { useLang, useScrollTo } from "@/context";

const LINKS = ["home", "services", "experience", "about", "fees", "contact"];

export default function Footer() {
  const { lang, setLang, t } = useLang();
  const scrollTo = useScrollTo();
  const navigate = useNavigate();
  const location = useLocation();

  const go = (id) => {
    if (id === "experience" || id === "about" || id === "fees" || id === "contact") {
      navigate(`/${id}`);
    } else if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      scrollTo(`#${id}`);
    }
  };

  return (
    <footer data-testid="footer" className="bg-charcoal text-ivory">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-20 sm:py-28">
        <div className="flex flex-col items-center text-center">
          <div className="bg-ivory px-10 sm:px-14 py-8 sm:py-10">
            <img
              data-testid="footer-logo"
              src="/brand/logo-full.png"
              alt="Merce Homar — Executive & Personal Assistant"
              className="h-52 sm:h-64 w-auto"
            />
          </div>
        </div>

        <nav className="mt-14 flex flex-wrap justify-center gap-x-8 gap-y-3" aria-label="Footer">
          {LINKS.map((id) => (
            <button
              key={id}
              data-testid={`footer-link-${id}`}
              onClick={() => go(id)}
              className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-taupe hover:text-gold transition-colors duration-300"
            >
              {t.nav[id]}
            </button>
          ))}
        </nav>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-5">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.2em]">
            <button
              data-testid="footer-lang-en"
              onClick={() => setLang("en")}
              className={lang === "en" ? "text-gold" : "text-taupe hover:text-ivory transition-colors"}
            >
              EN
            </button>
            <span className="text-taupe/50">|</span>
            <button
              data-testid="footer-lang-es"
              onClick={() => setLang("es")}
              className={lang === "es" ? "text-gold" : "text-taupe hover:text-ivory transition-colors"}
            >
              ES
            </button>
          </div>
          <button
            data-testid="footer-cta-lets-talk"
            onClick={() => go("contact")}
            className="border border-ivory/60 px-7 py-3 text-[10px] uppercase tracking-[0.24em] text-ivory hover:border-gold hover:text-gold transition-colors duration-300"
          >
            {t.contact.cta}
          </button>
          <a
            data-testid="footer-linkedin-link"
            href="https://www.linkedin.com/in/mercellina/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn — Merce Homar"
            className="border border-gold/30 hover:border-gold text-gold/80 hover:text-gold p-2 transition-colors duration-300"
          >
            <Linkedin size={14} />
          </a>
        </div>

        <div className="mt-8 flex items-center justify-center gap-8">
          <a
            data-testid="footer-brochure-link"
            href="/brochure/Merce-Homar-Executive-Support-Brochure.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-gold/80 hover:text-gold transition-colors duration-300 border-b border-gold/30 pb-1"
          >
            Service brochure (PDF · EN) ↓
          </a>
          <a
            data-testid="footer-brochure-es-link"
            href="/brochure/Merce-Homar-Dosier-de-Servicios.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-gold/80 hover:text-gold transition-colors duration-300 border-b border-gold/30 pb-1"
          >
            Dosier de servicios (PDF · ES) ↓
          </a>
        </div>

        <div className="mt-14 pt-8 border-t border-ivory/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-[0.22em] text-taupe/70">
          <span>© {new Date().getFullYear()} Merce Homar — {t.footer.rights}</span>
          <span data-testid="footer-privacy">{t.footer.privacy}</span>
          <button
            data-testid="footer-privacy-link"
            onClick={() => navigate("/privacy")}
            className="uppercase tracking-[0.22em] text-taupe hover:text-gold transition-colors duration-300"
          >
            {t.legal.label}
          </button>
          <span>{t.international.title}</span>
        </div>
      </div>
    </footer>
  );
}
